import { useEffect, useState } from "react";
import { AxonLockup } from "./AxonMark.js";

/** Public-facing strategies marketplace. Now hosted on its own
 *  subdomain (the standalone `axon-strategies` Vercel project bound
 *  to strategies.axonterminal.ai, marketplace-web served at root).
 *  The legacy `/strategies/` path on the unified landing deploy 301s
 *  here via landing/vercel.json so old links/bookmarks don't break.
 *  Exported so Marketplace + Footer share one source of truth. */
export const MARKETPLACE_URL = "https://strategies.axonterminal.ai";
/** Same canonical subdomain. Kept as a distinct export so existing
 *  call sites that wanted the absolute/cross-origin URL don't churn;
 *  origin deploy remains axon-strategies-strykr-labs.vercel.app. */
export const EXTERNAL_MARKETPLACE_URL = "https://strategies.axonterminal.ai";
/** The source repo is private (closed-source posture). The npm
 *  package page is the canonical public landing for the CLI — its
 *  README is the public-facing documentation until a hosted docs
 *  site ships. Everything that used to point at GitHub points here. */
export const NPM_URL = "https://www.npmjs.com/package/@axon-trading/cli";
export const NPM_SDK_URL = "https://www.npmjs.com/package/@axon-trading/sdk";
/** v0.15+ — docs site at docs.axonterminal.ai (Docusaurus). Replaces
 *  the previous "DOCS_URL → npm page" stopgap. The npm package page
 *  still exists for CLI install instructions, but `DOCS_URL` now
 *  routes through the canonical docs surface. */
export const DOCS_URL = "https://docs.axonterminal.ai";
/** Desktop app download — public release host (the source repo is
 *  private, so its release links 404 for anonymous users). Installers are
 *  mirrored to the public, releases-only `axon-desktop` repo; the hero's
 *  platform buttons deep-link to the resolved per-OS assets, while this
 *  URL is the "all platforms" releases page. */
export const DESKTOP_DOWNLOAD_URL =
  "https://github.com/Strykr-Labs/axon-desktop/releases/latest";
export const REPO_URL = NPM_URL;
/** Public skill + Claude Code plugin repo — the universal "install the
 *  skill" front door that works across every agent runtime. */
export const SKILL_REPO_URL = "https://github.com/Strykr-Labs/axon-skills";

export function Nav() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <nav className={"ax-nav" + (scrolled ? " scrolled" : "")}>
        <div className="ax-nav-inner">
          <a href="/" className="ax-brand" aria-label="Axon home">
            <AxonLockup size={26} />
          </a>

          <div className="ax-nav-links">
            <a href="#examples" className="ax-nav-link">Examples</a>
            <a href="#how" className="ax-nav-link">How it works</a>
            <a href={SKILL_REPO_URL} target="_blank" rel="noreferrer" className="ax-nav-link">Skill ↗</a>
            <a href={DOCS_URL} target="_blank" rel="noreferrer" className="ax-nav-link">Docs ↗</a>
            <a href={MARKETPLACE_URL} target="_blank" rel="noreferrer" className="ax-nav-link">Strategies ↗</a>
          </div>

          <div className="ax-nav-cta">
            <a href={DESKTOP_DOWNLOAD_URL} target="_blank" rel="noreferrer" className="ax-btn ax-btn-ghost">
              Download
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 3v12M6 9l6 6 6-6M5 21h14" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href={SKILL_REPO_URL} target="_blank" rel="noreferrer" className="ax-btn ax-btn-primary">
              Get the skill
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>

        <style>{`
          .ax-nav {
            position: fixed; top: 0; left: 0; right: 0;
            z-index: 50;
            background: rgba(10, 11, 13, 0.72);
            backdrop-filter: blur(14px);
            -webkit-backdrop-filter: blur(14px);
            border-bottom: 1px solid transparent;
            transition: border-color var(--at-dur-med) var(--at-ease),
                        background var(--at-dur-med) var(--at-ease);
          }
          .ax-nav.scrolled {
            background: rgba(10, 11, 13, 0.88);
            border-bottom-color: var(--at-hairline);
          }
          .ax-nav-inner {
            /* Track the same fluid container width as .ax-section so
               the brand mark + CTA align with the hero content edges
               on ultrawide monitors instead of floating in the middle. */
            max-width: min(1800px, 92vw); margin: 0 auto;
            padding: 16px clamp(20px, 5vw, 96px);
            display: grid; grid-template-columns: auto 1fr auto;
            gap: clamp(20px, 3vw, 48px); align-items: center;
          }
          .ax-brand { display: inline-flex; align-items: center; }
          .ax-nav-links {
            display: flex; gap: 28px; justify-content: center;
          }
          .ax-nav-link {
            display: inline-flex; align-items: center; gap: 5px;
            font-size: 14px; color: var(--at-fg-dim);
            transition: color var(--at-dur-fast) var(--at-ease);
          }
          .ax-nav-link:hover { color: var(--at-fg); }
          .ax-nav-cta { display: inline-flex; gap: 8px; }

          @media (max-width: 900px) {
            .ax-nav-inner { grid-template-columns: auto auto; padding: 14px 20px; }
            .ax-nav-links { display: none; }
          }
          @media (max-width: 540px) {
            .ax-nav-cta .ax-btn-ghost { display: none; }
          }
        `}</style>
      </nav>
      {/* Spacer to offset fixed nav */}
      <div style={{ height: 70 }} />
    </>
  );
}
